import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Select } from '~/eds/src/components/Select/Select';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { QueryScope } from '~/features/advanced-search/query-filters/QueryScope';
import { SearchQuery } from '~/features/advanced-search/types';
import { useRestrictedFilterIds } from '~/features/quick-ai/hooks';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';

import { ProvisionsType, ProvisionSuggestions } from './types';

const MAX_SELECTED_PROVISION_TYPES = 5;

interface Props {
  createProvision: (
    provisionType: string,
    applyToAll: boolean,
    query: SearchQuery,
  ) => void;
  hideModal: () => void;
  bulkTaggingInsufficientWords: boolean;
  preselectedProvision?: string;
}

export const TagClauseModal = ({
  createProvision,
  hideModal,
  bulkTaggingInsufficientWords,
  preselectedProvision,
}: Props) => {
  const intl = useIntl();
  const enableQuickAiFilters = useFlag(FlagType.QuickAiFilters);
  const isClauseTaggingDisabled = useHasFeatureFlag(
    FeatureFlagType.DisableDocTagging,
  );
  const initialProvisionTypes = preselectedProvision
    ? [preselectedProvision]
    : [];

  const placeholder = intl.formatMessage({
    id: 'TagClauseModal.selectClausesPlaceholder',
    defaultMessage: 'Select Clauses',
    description: 'Placeholder for clause selection field',
  });

  const [selectedProvisionTypes, setSelectedProvisionTypes] =
    useState<ProvisionsType>(initialProvisionTypes);

  const [includesExistingProvisions, setIncludesExistingProvisions] =
    useState(false);

  const [query, setQuery] = useState<SearchQuery>([]);

  const [selectedApplyTo, setSelectedApplyTo] = useState(false);
  const [exceedsProvisionTypeLimit, setExceedsProvisionTypeLimit] =
    useState(false);
  const [provisionTypes, setProvisionTypes] = useState<ProvisionSuggestions[]>(
    [],
  );
  const restrictedClauseFilterIds = useRestrictedFilterIds();

  const { data: suggestions, isLoading: isLoadingSuggestions } =
    api.endpoints.getClauseSuggestions.useQuery(undefined);

  useEffect(() => {
    if (suggestions)
      setProvisionTypes(
        suggestions.suggestions.map((key: string) => ({
          label: key,
          value: key,
        })),
      );
  }, [suggestions]);

  useEffect(() => {
    setExceedsProvisionTypeLimit(
      Boolean(
        selectedProvisionTypes &&
        selectedProvisionTypes.length > MAX_SELECTED_PROVISION_TYPES,
      ),
    );
  }, [selectedProvisionTypes]);

  useEffect(() => {
    if (selectedProvisionTypes) {
      const hasExistingProvision = selectedProvisionTypes.some(
        (provisionType) => {
          return suggestions?.suggestions.includes(provisionType);
        },
      );
      setIncludesExistingProvisions(hasExistingProvision);
    }
  }, [selectedProvisionTypes, suggestions]);

  const shouldDisableClauseTagging =
    isClauseTaggingDisabled || bulkTaggingInsufficientWords;

  const navigateToAutomationHub = {
    additionalText: intl.formatMessage({
      id: 'TagClauseModal.existingFiltersText',
      defaultMessage: 'Existing filters can only be managed in',
      description: 'Text explaining where existing filters can be managed',
    }),
    linkText: intl.formatMessage({
      id: 'TagClauseModal.automationHubLink',
      defaultMessage: 'Automation Hub',
      description: 'Link text for Automation Hub',
    }),
    pathname: `${RoutePathType.AutomationHub}`,
    tooltip: intl.formatMessage({
      id: 'TagClauseModal.automationHubTooltip',
      defaultMessage: 'Go to Automation Hub page',
      description: 'Tooltip for Automation Hub link',
    }),
  };

  const activeQuery = isActiveQuery(query);

  return (
    <Modal
      width="l"
      isVisible
      onHide={hideModal}
      title={intl.formatMessage({
        id: 'TagClauseModal.title',
        defaultMessage: 'Quick AI',
        description: 'Title for Quick AI modal',
      })}
      primaryAction={{
        disabled:
          selectedProvisionTypes?.length === 0 || exceedsProvisionTypeLimit,
        tooltip:
          selectedProvisionTypes?.length === 0
            ? intl.formatMessage({
                id: 'TagClauseModal.selectClauseTooltip',
                defaultMessage: 'Select at least one Clause',
                description: 'Tooltip when no clauses are selected',
              })
            : exceedsProvisionTypeLimit
              ? intl.formatMessage(
                  {
                    id: 'TagClauseModal.maxClausesTooltip',
                    defaultMessage:
                      'Maximum of {maxSelections} Clause selections',
                    description:
                      'Tooltip when maximum clauses exceeded. Example: maxSelections = 5',
                  },
                  { maxSelections: MAX_SELECTED_PROVISION_TYPES },
                )
              : undefined,
        text: intl.formatMessage({
          id: 'TagClauseModal.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text',
        }),
        onClick: () => {
          if (selectedProvisionTypes)
            selectedProvisionTypes.forEach((selectedProvisionType) => {
              const query =
                !includesExistingProvisions && selectedApplyTo
                  ? activeQuery
                  : [];

              createProvision(selectedProvisionType, selectedApplyTo, query);
            });
          hideModal();
        },
      }}
    >
      <Layout preset="sections">
        <Layout preset="form-fields">
          <FormField<ProvisionsType, false>
            id="select-clause-tagging-names"
            required
            footer={intl.formatMessage({
              id: 'TagClauseModal.maxSelectionsFooter',
              defaultMessage: 'Maximum of 5 Clause selections',
              description:
                'Footer text showing maximum clause selections allowed',
            })}
            label={intl.formatMessage({
              id: 'TagClauseModal.clauseLabel',
              defaultMessage: 'Clause',
              description: 'Label for clause selection field',
            })}
            input={Select}
            value={selectedProvisionTypes}
            onChange={setSelectedProvisionTypes}
            name="Clause"
            error={
              exceedsProvisionTypeLimit
                ? intl.formatMessage(
                    {
                      id: 'TagClauseModal.maxSelectionsError',
                      defaultMessage:
                        'Maximum of {maxSelections} Clause selections',
                      description:
                        'Error message when maximum selections exceeded. Example: maxSelections = 5',
                    },
                    { maxSelections: MAX_SELECTED_PROVISION_TYPES },
                  )
                : undefined
            }
            description={intl.formatMessage({
              id: 'TagClauseModal.clauseDescription',
              defaultMessage:
                'The Clause(s) that this language will be associated with when identified',
              description: 'Description for clause selection field',
            })}
            placeholder={placeholder}
            inputProps={{
              isLoading: isLoadingSuggestions,
              isMulti: true,
              onAddOption: () => setSelectedProvisionTypes,
              options: provisionTypes,
            }}
          />
          <FormField<boolean, false>
            id="select-clause-tagging-type"
            label={intl.formatMessage({
              id: 'TagClauseModal.applySampleToLabel',
              defaultMessage: 'Apply Sample to',
              description: 'Label for apply sample to field',
            })}
            name="Apply Sample to"
            required
            input={Select}
            value={selectedApplyTo}
            onChange={(value) => {
              setSelectedApplyTo(value ?? false);
            }}
            placeholder={placeholder}
            inputProps={{
              disabled: shouldDisableClauseTagging,
              isMulti: false,
              isClearable: false,
              options: [
                {
                  label: intl.formatMessage({
                    id: 'TagClauseModal.onlyThisDocumentOption',
                    defaultMessage: 'Only this document',
                    description: 'Option to apply to only current document',
                  }),
                  value: false,
                },
                {
                  label: intl.formatMessage({
                    id: 'TagClauseModal.thisAndOtherDocumentsOption',
                    defaultMessage: 'This and other documents',
                    description:
                      'Option to apply to current and other documents',
                  }),
                  value: true,
                },
              ],
            }}
          />
        </Layout>
        {selectedApplyTo && !preselectedProvision && enableQuickAiFilters && (
          <Layout preset="sections">
            <QueryFiltersFormField
              title={intl.formatMessage({
                id: 'TagClauseModal.documentFiltersTitle',
                defaultMessage: 'Document Filters',
                description: 'Title for document filters section',
              })}
              defaultFilters={['folder', 'Contract Type', 'Effective Date']}
              description={
                includesExistingProvisions
                  ? undefined
                  : intl.formatMessage({
                      id: 'TagClauseModal.filtersDescription',
                      defaultMessage:
                        'Filters will be used to select documents that contain this exact clause',
                      description: 'Description for document filters',
                    })
              }
              descriptionLink={
                includesExistingProvisions ? navigateToAutomationHub : undefined
              }
              query={query}
              onQueryChange={(updatedQuery) => {
                setQuery(updatedQuery);
              }}
              readOnly={includesExistingProvisions}
              restrictedFilterIds={restrictedClauseFilterIds}
            />
            <QueryScope query={query} />
          </Layout>
        )}
      </Layout>
    </Modal>
  );
};
